import { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../hooks/auth';
import { FiLogOut, FiUsers, FiShield } from 'react-icons/fi';
// import { FcGoogle } from 'react-icons/fc';
import logoImg from '../../assets/t4h_preto.png';
import { faClock, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import SessionTimer from '../sessionTimer';

const AUTH_KEYS = {
  AUTHORIZATION: 'Authorization',
  AUTHORIZATION_RA: 'AuthorizationRA',
  AUTHORIZATION_FORCE: 'Authorization_force',
  CLIENT_ID_FORCE: 'clientIdForce',
  SESSION: 'SESSION_KEY',
  LOGOUT_TIMEOUT: 'LOGOUT_TIMEOUT_KEY',
  SLUG: 'slug',
  AMBIENT: 'ambient_to',
  IN_FORCE: 'inForce',
} as const;

export function Header() {
  const { user, signOut } = useAuthContext();
  const [search, setSearch] = useState('');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const storedInForce = localStorage.getItem(AUTH_KEYS.IN_FORCE);
  let rcForce: string;

  if (storedInForce === "false") {
    rcForce = "RC";
  } else {
    rcForce = "FORCE";
  }

  const handleSearchForm = () => {
    navigate(`/section/listForms?search=${encodeURIComponent(search)}`);
  }

  const handleGoHome = () => {
    navigate('/section');
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    setSearch(urlSearch);
  }, [location.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  return (
    <>
      <header className="header-shell">
        <div className="header-top">
          <div className="header-left">
            <button onClick={handleGoHome} className="header-logo" type="button" aria-label="Ir para home">
              <img src={logoImg} alt="logo" />
            </button>
          </div>

          <div className="header-right">
            <div className="header-session">
              <FontAwesomeIcon icon={faClock} />
              <span className="header-session-label">Sessão</span>
              <SessionTimer />
            </div>

            <span className="header-divider" />

            <div className="header-user-info">
              <span className="header-user-role">{user?.google?.name || user?.userName || user?.userEmail || "Administrator"}</span>
            </div>

            <div ref={userMenuRef} className="header-user">
              <button
                onClick={() => setIsUserMenuOpen((prev) => !prev)}
                className="header-avatar"
                aria-label="Menu do usuário"
                aria-expanded={isUserMenuOpen}
                type="button"
              >
                {user?.google?.picture ? (
                  <img src={user?.google?.picture} alt="Avatar" referrerPolicy="no-referrer" />
                ) : (
                  <FontAwesomeIcon icon={faUser} />
                )}
              </button>
              {isUserMenuOpen && (
                <div className="header-user-menu">
                  {user?.role === 'admin' && (
                    <>
                      <button
                        type="button"
                        className="bg-button-clients flex items-center gap-2.5 px-4 py-3 w-full cursor-pointer hover:bg-gray-50 transition-colors text-left"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/section/clients');
                        }}
                      >
                        <FiUsers size={18} className="text-gray-700 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700">Clientes</span>
                      </button>
                      <button
                        type="button"
                        className="bg-button-users flex items-center gap-2.5 px-4 py-3 w-full cursor-pointer hover:bg-gray-50 transition-colors text-left"
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          navigate('/section/users');
                        }}
                      >
                        <FiShield size={18} className="text-gray-700 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700">Usuários</span>
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="bg-button-logout flex items-center gap-2.5 px-4 py-3 w-full cursor-pointer hover:bg-gray-50 transition-colors text-left"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      signOut();
                    }}
                  >
                    <FiLogOut size={18} className="text-gray-700 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700">Sair</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <nav className="header-nav">
          <div className="header-nav-left">
            {[
              { href: `/section/listForms?clientId=${user?.clientId}`, label: 'Listar Formulários', path: 'listForms' },
              { href: `/section/createForm?clientId=${user?.clientId}`, label: 'Criar novo formulário', path: 'createForm', create: true, mode: 'create' },
              { href: `/section/createTemplate?clientId=${user?.clientId}`, label: 'Criar form pelo payload', path: 'createTemplate' },
              { href: `/section/tabulation?clientId=${user?.clientId}`, label: 'Tabulação em massa', path: 'tabulation' },
              { href: `/section/planTabulations?clientId=${user?.clientId}`, label: 'Planilha (tabulações)', path: 'planTabulations' },
              { href: `/section/createSkills?clientId=${user?.clientId}`, label: 'Criar Skills', path: 'createSkills' },
              { href: `/section/pendency?clientId=${user?.clientId}`, label: 'Responder pendencias em massa', path: 'pendency' },
              { href: `/section/metrics`, label: 'Métricas', path: 'metrics' },
            ].map((tab) => {
              const currentBasePath = location.pathname.split('/').pop() || '';
              const isActive = currentBasePath === tab.path;
              return (
                <div key={tab.label} className="flex-shrink-0">
                  <NavLink to={tab.href} tabIndex={0} data-testid="Tab">
                    <div className={`header-tab ${isActive ? 'is-active' : ''}`}>
                      {tab.label}
                    </div>
                  </NavLink>
                </div>
              );
            })}
          </div>
          <div className="header-nav-right">
            <div className="header-badges">
              <span className="header-tag is-blue">{user?.clientName || "Cliente"}</span>
              <span className="header-tag is-purple">{rcForce}</span>
              <span className="header-tag is-green">{user?.ambientLogged === 'hml' ? 'HOMOLOGAÇÃO' : 'PRODUÇÃO'}</span>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}